import { useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Analytics } from '@vercel/analytics/react'
import { track } from '@vercel/analytics'
import { LanguageProvider, useLanguage } from './contexts/LanguageContext'
import { BunnyLevel } from './components/activities/AnimalJourney/BunnyLevel'
import { TreeLevel } from './components/activities/AnimalJourney/TreeLevel'
import { PekeLevel } from './components/activities/AnimalJourney/PekeLevel'
import { EchoDrawGame } from './components/echoDraw'
import { CreatureSuperpowersGame } from './components/creatureSuperpowers'
import { SillyAnimalGenerator } from './components/sillyAnimalGenerator'
import { AnimalABCs } from './components/games/AnimalABCs'
import { initAudioUnlock } from './lib/naturalVoiceService'
import { FAQ } from './components/FAQ'
import { Toaster } from './components/ui/sonner'
import { GlobalNav } from './components/layout/GlobalNav'
import { WonderLittleHomeGrid } from './components/earlyChildhood/WonderLittleHomeGrid'
import { AnimatedFlowerBackground } from './components/backgrounds'
import PlayTogetherFloatingHub from './components/earlyChildhood/PlayTogetherFloatingHub'
import { PLAY_TOGETHER_MICRO_SCRIPT_REVISION } from './data/playTogetherMicroOnboarding'
import type { HomeGameScreen } from './data/homeGameCards'
import { PasscodeGate } from './components/PasscodeGate'
import { SpeakingSubtitle } from './components/ui/SpeakingSubtitle'

initAudioUnlock();

type Screen = 'welcome' | 'bunny-level' | 'tree-level' | 'peke-level' | 'echo-draw' | 'creature-superpowers' | 'silly-animal-generator' | 'animal-abcs' | 'faq';

function AppContent() {
  const [currentScreen, setCurrentScreen] = useState<Screen>('welcome')
  const [isTransitioning, setIsTransitioning] = useState(false)
  const [transitionColor, setTransitionColor] = useState('')
  const { language, setLanguage } = useLanguage()

  const greetings = {
    en: {
      title: 'Welcome to Wonderpath',
      subtitle: 'nature-based learning through play for curious beginnings',
      description: 'Choose Your Adventure!',
      animalABCsTitle: 'Animal ABCs, 123s',
      animalABCsDesc: 'Learn letters & numbers with animals!',
      animalABCsButton: 'Draw & Learn!',
      treeTitle: 'Pepe Knows',
      treeDesc: 'Learn with Pepe the wise tree!',
      treeButton: 'Meet Pepe the Tree!',
      pekeTitle: 'Peke\'s Adventures',
      pekeDesc: 'Join Peke the Boston Terrier on exciting adventures',
      pekeButton: 'Adventure with Peke!',
      echoDrawTitle: 'EchoDraw',
      echoDrawDesc: 'Where Sound Becomes Color and Color Becomes Sound',
      echoDrawButton: 'Make Sounds into Colors!',
      creatureSuperpowersTitle: 'Superpowers',
      creatureSuperpowersDesc: 'Choose a creature, give it superpowers, and draw it',
      creatureSuperpowersButton: 'Create Your Creature!',
      middleGradesTitle: 'Creative Trailblazers',
      middleGradesDesc: 'Advanced creative games',
      middleGradesButton: 'Explore More Games',
      ecoDesignLabTitle: 'EcoDesign Lab',
      ecoDesignLabDesc: 'Start your learning in sustainable design',
      adultsTitle: 'Drawing Games for Adults',
      adultsDesc: 'Creative Exploration',
      adultsButton: 'Explore',
      crossAppBannerTitle: 'Creative games for every age.',
      feedbackButton: 'Share your Feedback',
      feedbackDesc: 'Help shape what comes next! Tell us what activities your child would love.',
      formUrl: 'https://docs.google.com/forms/d/e/1FAIpQLSfVAgud-TcPVd9t1Ut7SgfJcO08t9WXT-FvtskpFcJwEcQT1w/viewform',
      helpFAQ: 'Help & FAQ',
    },
    es: {
      title: 'Bienvenido a Wonderpath',
      subtitle: 'aprendizaje basado en la naturaleza a través del juego para comienzos curiosos',
      description: '¡Elige Tu Aventura!',
      animalABCsTitle: 'ABC de Animales, 123',
      animalABCsDesc: '¡Aprende letras y números con animales!',
      animalABCsButton: '¡Dibuja y Aprende!',
      treeTitle: 'Pepe Sabe',
      treeDesc: '¡Aprende con Pepe el árbol sabio!',
      treeButton: '¡Conoce a Pepe el Árbol!',
      pekeTitle: 'Las Aventuras de Peke',
      pekeDesc: 'Únete a Peke el Boston Terrier en emocionantes aventuras',
      pekeButton: '¡Aventura con Peke!',
      echoDrawTitle: 'EchoDraw',
      echoDrawDesc: 'Donde el Sonido se Convierte en Color y el Color en Sonido',
      echoDrawButton: '¡Convierte Sonidos en Colores!',
      creatureSuperpowersTitle: 'Superpoderes',
      creatureSuperpowersDesc: 'Elige una criatura, dale superpoderes y dibújala',
      creatureSuperpowersButton: '¡Crea Tu Criatura!',
      middleGradesTitle: 'Pioneros Creativos',
      middleGradesDesc: 'Juegos creativos avanzados',
      middleGradesButton: 'Explorar Más Juegos',
      ecoDesignLabTitle: 'Laboratorio de EcoDiseño',
      ecoDesignLabDesc: 'Comienza tu aprendizaje en diseño sostenible',
      adultsTitle: 'Juegos de Dibujo para Adultos',
      adultsDesc: 'Exploración Creativa',
      adultsButton: 'Explorar',
      crossAppBannerTitle: 'Juegos creativos para todas las edades.',
      feedbackButton: 'Comparte tu Feedback',
      feedbackDesc: '¡Ayúdanos a dar forma a lo que viene! Cuéntanos qué actividades le encantarían a tu hijo/a.',
      formUrl: 'https://forms.google.com/YOUR_SPANISH_FORM_ID',
      helpFAQ: 'Ayuda y Preguntas Frecuentes',
    },
  }

  const [playTogetherOnboardingDone, setPlayTogetherOnboardingDone] = useState(() => {
    try {
      if (typeof window === 'undefined') return false;
      /** `?replayPlayTogether=1` — show welcome onboarding even if it was completed before */
      const search = new URLSearchParams(window.location.search);
      if (search.get('replayPlayTogether') === '1') {
        return false;
      }
      return localStorage.getItem('wp_play_together_onboarding_v1') === '1';
    } catch {
      return false;
    }
  });

  const markPlayTogetherOnboardingDone = () => {
    try {
      localStorage.setItem('wp_play_together_onboarding_v1', '1');
    } catch {
      /* ignore */
    }
    setPlayTogetherOnboardingDone(true);
  };

  const navigateToGameFromPlayTogether = (screen: HomeGameScreen) => {
    const colors: Record<HomeGameScreen, string> = {
      'animal-abcs': '#5EBD8B',
      'tree-level': '#48A589',
      'creature-superpowers': '#358A73',
      'peke-level': '#3BB257',
    };
    track('Play Together Landing', {
      action: 'pick_game',
      game: screen,
      language,
    });
    setTransitionColor(colors[screen]);
    setIsTransitioning(true);
    setTimeout(() => {
      setCurrentScreen(screen);
      setIsTransitioning(false);
    }, 500);
  };

  /** Remount when script revision bumps so chat state cannot keep stale welcome/copy from an old bundle. */
  const playTogetherHub = (
    <PlayTogetherFloatingHub
      key={`play-together-${PLAY_TOGETHER_MICRO_SCRIPT_REVISION}`}
      initialLandingOpen={currentScreen === 'welcome' && !playTogetherOnboardingDone}
      onLandingDismiss={markPlayTogetherOnboardingDone}
      onNavigateToGame={navigateToGameFromPlayTogether}
    />
  );

  if (currentScreen === 'bunny-level') {
    return (
      <>
        <div className="w-full h-full flex flex-col overflow-y-auto">
          <GlobalNav onBackToHome={() => setCurrentScreen('welcome')} />
          <div className="flex-1">
            <BunnyLevel onBackToHome={() => setCurrentScreen('welcome')} />
          </div>
          <Footer language={language} onFAQClick={() => setCurrentScreen('faq')} />
        </div>
        {playTogetherHub}
      </>
    );
  }

  if (currentScreen === 'tree-level') {
    return (
      <>
        <div className="w-full h-full flex flex-col overflow-y-auto">
          <GlobalNav onBackToHome={() => setCurrentScreen('welcome')} />
          <div className="flex-1">
            <TreeLevel onBackToHome={() => setCurrentScreen('welcome')} />
          </div>
          <Footer language={language} onFAQClick={() => setCurrentScreen('faq')} />
        </div>
        {playTogetherHub}
      </>
    );
  }

  if (currentScreen === 'peke-level') {
    return (
      <>
        <div className="w-full h-full flex flex-col overflow-y-auto">
          <GlobalNav onBackToHome={() => setCurrentScreen('welcome')} />
          <div className="flex-1">
            <PekeLevel onBackToHome={() => setCurrentScreen('welcome')} />
          </div>
          <Footer language={language} onFAQClick={() => setCurrentScreen('faq')} />
        </div>
        {playTogetherHub}
      </>
    );
  }

  if (currentScreen === 'echo-draw') {
    return (
      <>
        <div className="w-full h-full flex flex-col overflow-y-auto">
          <GlobalNav onBackToHome={() => setCurrentScreen('welcome')} />
          <div className="flex-1">
            <EchoDrawGame
              language={language as 'en' | 'es'}
              onExit={() => setCurrentScreen('welcome')}
              onLanguageChange={() => setLanguage(language === 'en' ? 'es' : 'en')}
            />
          </div>
          <Footer language={language} onFAQClick={() => setCurrentScreen('faq')} />
        </div>
        {playTogetherHub}
      </>
    );
  }

  if (currentScreen === 'creature-superpowers') {
    return (
      <>
        <div className="w-full h-full flex flex-col overflow-y-auto">
          <GlobalNav onBackToHome={() => setCurrentScreen('welcome')} />
          <div className="flex-1">
            <CreatureSuperpowersGame
              language={language as 'en' | 'es'}
              onExit={() => setCurrentScreen('welcome')}
              onLanguageToggle={() => setLanguage(language === 'en' ? 'es' : 'en')}
            />
          </div>
          <Footer language={language} onFAQClick={() => setCurrentScreen('faq')} />
        </div>
        {playTogetherHub}
      </>
    );
  }

  if (currentScreen === 'silly-animal-generator') {
    return (
      <>
        <div className="w-full h-full flex flex-col overflow-y-auto">
          <GlobalNav onBackToHome={() => setCurrentScreen('welcome')} />
          <div className="flex-1">
            <SillyAnimalGenerator
              language={language as 'en' | 'es'}
              onExit={() => setCurrentScreen('welcome')}
              onLanguageToggle={() => setLanguage(language === 'en' ? 'es' : 'en')}
            />
          </div>
          <Footer language={language} onFAQClick={() => setCurrentScreen('faq')} />
        </div>
        {playTogetherHub}
      </>
    );
  }

  if (currentScreen === 'animal-abcs') {
    return (
      <>
        <div className="w-full h-full flex flex-col overflow-y-auto">
          <GlobalNav onBackToHome={() => setCurrentScreen('welcome')} />
          <div className="flex-1">
            <AnimalABCs />
          </div>
          <Footer language={language} onFAQClick={() => setCurrentScreen('faq')} />
        </div>
        {playTogetherHub}
      </>
    );
  }

  if (currentScreen === 'faq') {
    return (
      <>
        <div className="w-full h-full flex flex-col overflow-y-auto">
          <GlobalNav onBackToHome={() => setCurrentScreen('welcome')} />
          <div className="flex-1">
            <FAQ onBack={() => setCurrentScreen('welcome')} />
          </div>
          <Footer language={language} onFAQClick={() => setCurrentScreen('faq')} />
        </div>
        {playTogetherHub}
      </>
    );
  }

  return (
    <div className="w-full h-full flex flex-col overflow-y-auto relative">
      {/* Animated Flower Background - OLD: bg-gradient-to-b from-primary-sky to-primary-grass */}
      <AnimatedFlowerBackground theme="bubbles" />

      {/* Tile Expansion Transition Overlay */}
      <AnimatePresence>
        {isTransitioning && (
          <motion.div
            initial={{ scale: 0, borderRadius: '2rem', opacity: 1 }}
            animate={{ scale: 20, borderRadius: '0%', opacity: 1 }}
            exit={{ opacity: 0, scale: 25 }}
            transition={{
              duration: 0.5,
              ease: [0.43, 0.13, 0.23, 0.96], // Custom easing for smooth expansion
              exit: { duration: 0.2, ease: 'easeOut' } // Faster, smoother fade out
            }}
            className="fixed inset-0 z-[100] pointer-events-none"
            style={{
              background: transitionColor,
              transformOrigin: 'center center'
            }}
          />
        )}
      </AnimatePresence>

      <div className="relative z-10">
        <GlobalNav />
      </div>
      <div className="relative z-10 flex-1 pt-6 pb-12 w-full">
        <WonderLittleHomeGrid
          strings={greetings[language]}
          language={language}
          onSelectGame={(screen, color) => {
            markPlayTogetherOnboardingDone();
            setTransitionColor(color);
            setIsTransitioning(true);
            setTimeout(() => {
              setCurrentScreen(screen);
              setIsTransitioning(false);
            }, 500);
          }}
        />
      </div>

      <div className="relative z-10">
        <Footer language={language} onFAQClick={() => setCurrentScreen('faq')} />
      </div>

      {/* Toast Notifications */}
      <Toaster />
      {playTogetherHub}
    </div>
  )
}

// Footer Component with Legal Links
function Footer({ language, onFAQClick }: { language: 'en' | 'es'; onFAQClick?: () => void }) {
  const footerText = {
    en: {
      copyright: '© Wonderpath LLC, 2026. Wonderpath Eco Games. All rights reserved.',
      subtitle: 'Free educational tool for creative learning about ecology.',
      privacy: 'Privacy Policy',
      terms: 'Terms of Service',
      communityGuidelines: 'Community Guidelines',
      about: 'About',
      contact: 'Contact Us',
      helpFAQ: 'Help & FAQ',
    },
    es: {
      copyright: '© Wonderpath LLC, 2026. Wonderpath Eco Games. Todos los derechos reservados.',
      subtitle: 'Herramienta educativa gratuita para el aprendizaje creativo sobre ecología.',
      privacy: 'Política de Privacidad',
      terms: 'Términos de Servicio',
      communityGuidelines: 'Pautas de la Comunidad',
      about: 'Acerca de',
      contact: 'Contáctanos',
      helpFAQ: 'Ayuda y Preguntas Frecuentes',
    },
  };

  const text = footerText[language];

  return (
    <footer className="w-full backdrop-blur-sm py-4 px-6 mt-auto" style={{ backgroundColor: 'rgba(5, 150, 105, 0.65)' }}>
      <div className="max-w-6xl mx-auto flex flex-col items-center gap-3 text-white/90 text-sm">
        <div className="text-center">
          <p className="text-xs sm:text-sm font-medium">{text.copyright} • {text.subtitle}</p>
        </div>
        <div className="flex flex-wrap gap-4 sm:gap-6 justify-center">
          {onFAQClick && (
            <button
              onClick={onFAQClick}
              className="hover:text-white transition-colors underline"
            >
              {text.helpFAQ}
            </button>
          )}
          <a
            href="https://eco-games-beta.vercel.app/terms-of-service"
            className="hover:text-white transition-colors underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            {text.terms}
          </a>
          <a
            href="https://eco-games-beta.vercel.app/privacy-policy"
            className="hover:text-white transition-colors underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            {text.privacy}
          </a>
          <a
            href="https://eco-games-beta.vercel.app/community-guidelines"
            className="hover:text-white transition-colors underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            {text.communityGuidelines}
          </a>
          <a
            href="https://eco-games-beta.vercel.app/about"
            className="hover:text-white transition-colors underline"
            target="_blank"
            rel="noopener noreferrer"
          >
            {text.about}
          </a>
          <a
            href="mailto:info@wonderpath.nyc"
            className="hover:text-white transition-colors underline"
          >
            {text.contact}
          </a>
        </div>
      </div>
    </footer>
  );
}

function App() {
  return (
    <LanguageProvider>
      <PasscodeGate>
        <AppContent />
        {/* Single global subtitle bar — every speak() across the app
            surfaces here. Mounted above PasscodeGate's children so it
            sits above any phase / hub overlay. */}
        <SpeakingSubtitle />
        <Analytics />
      </PasscodeGate>
    </LanguageProvider>
  )
}

export default App
